import { Fragment, useState } from 'react';

import { Backup, BackupKind, BackupStorageKind, Customer, LoginMembership, Website } from 'src/orchd-client';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';

import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Card } from 'src/components/Card';
import { DropdownMenu } from 'src/components/DropdownMenu';
import { Icon } from 'src/components/Icon';
import { LegendNumber } from 'src/components/LegendNumber/LegendNumber';
import { SquarePill } from 'src/components/SquarePill/SquarePill';
import { Span, Text } from 'src/components/Text';
import { Tooltip } from 'src/components/Tooltip';
import { viewportSelectors } from 'src/containers/Viewport/_redux/selectors';
import { useWebsiteSubscription } from 'src/hooks/useSubscriptions/useSubscriptions';
import { messages } from 'src/messages';
import { SetRestoreState, SetWebsiteBackup } from 'src/pages/websites/website/backups/WebsiteBackupModal';
import { AppState } from 'src/store';
import { dateConfig, timeConfig } from 'src/translations/constants';
import { getByteString } from 'src/utils/helpers';
import { PackageAllowanceName } from 'src/utils/packages/types';
import { getSubscriptionAllowance } from 'src/utils/packages/utils';

const mapStateToProps = (state: AppState) => ({
  isMobile: viewportSelectors.isMobile(state),
});

type StateProps = ReturnType<typeof mapStateToProps>;

export type VaultInfo = {
  serverId: string;
  domain: string;
  websiteId: string;
  createdAt: string;
  restoreVaultBackup: (vaultInfo: VaultInfo) => {};
  customer: Customer;
};

interface Props extends StateProps {
  member: LoginMembership;
  website: Website;
  websiteBackup: Backup;
  creatingBackup: boolean;
  setRestoreState: SetRestoreState;
  setWebsiteBackup: SetWebsiteBackup;
  showEmails: boolean;
  vaultInfo?: VaultInfo;
  searchTerm?: string;
  handleDeleteBackups?: (vaultInfo: VaultInfo) => {};
}

export const getBackupDateTime = (timestamp: string) => (
  <span>
    <FormattedDate value={timestamp} {...dateConfig} /> at <FormattedDate value={timestamp} {...timeConfig} />
  </span>
);

export const _WebsiteBackupCard = ({
  isMobile,
  websiteBackup,
  setWebsiteBackup,
  setRestoreState,
  member,
  website,
  creatingBackup,
  showEmails,
  vaultInfo,
  searchTerm,
  handleDeleteBackups,
}: Props) => {
  const { orgId } = website;
  const {
    emailsCount,
    emailsSize,
    filesSize,
    id,
    kind,
    mysqlDbsCount,
    mysqlDbsSize,
    startedAt,
    description,
    storageKind,
    postgresqlDbsCount,
  } = websiteBackup;

  const totalDbsCount = mysqlDbsCount + postgresqlDbsCount;

  const [restoreInitiated, setRestoreInitiated] = useState(false);

  const noFilesBackup = filesSize === null || filesSize === 0;
  const noMysqlDbsBackup = mysqlDbsSize === null;
  const emailBackupError = emailsCount === null || emailsSize === null;
  const noEmailsBackup = emailBackupError || emailsCount === 0;

  const { subscription } = useWebsiteSubscription({ orgId, website });

  const dropdownDisabled = [noFilesBackup, noMysqlDbsBackup, noEmailsBackup].every((failed) => failed);

  const renderRestoreButtonUser = () => {
    const show =
      member.isMasterOrg || getSubscriptionAllowance(subscription, PackageAllowanceName.backupsAllowSelfRestore);

    if (!show) {
      return null;
    }
    const options = [];

    if (!noEmailsBackup) {
      options.push({
        label: <FormattedMessage id="backup_restore.all_mailboxes" defaultMessage="All mailboxes" />,
        onClick: () => {
          setWebsiteBackup(websiteBackup);
          setRestoreState({
            restoreFiles: false,
            restoreDatabases: [],
            restoreAllEmails: true,
          });
        },
        disabled: noEmailsBackup,
      });
    }

    if (!noFilesBackup) {
      options.push({
        label: <FormattedMessage id="backup_restore.full_website" defaultMessage="Full website" />,
        onClick: () => {
          setWebsiteBackup(websiteBackup);
          setRestoreState({ restoreFiles: true, restoreAllEmails: false });
        },
        disabled: noFilesBackup,
      });
    }

    options.push({
      label: <FormattedMessage {...messages.custom} />,
      url: `/websites/${website.id}/backups/${id}/restore?storageKind=${websiteBackup.storageKind}`,
    });

    return (
      <Box w="auto">
        <DropdownMenu
          button={
            <Button
              icon={<Icon name="chevron down" hasHoverClass />}
              variant="secondary"
              disabled={dropdownDisabled || creatingBackup}
              data-qa="restoreDropdownButton"
              iconPosition="right"
              size="small"
            >
              <FormattedMessage {...messages.restore} />
            </Button>
          }
          options={options}
        />
      </Box>
    );
  };

  const renderRestoreButtonVault = () => {
    return (
      <Box w="auto">
        <Button
          variant="secondary"
          disabled={restoreInitiated}
          data-qa="restoreDropdownButton"
          iconPosition="right"
          size="small"
          onClick={() => {
            setRestoreInitiated(true);
            vaultInfo.restoreVaultBackup(vaultInfo);
          }}
          loading={restoreInitiated}
          marginRight="sm"
        >
          <FormattedMessage {...messages.restore} />
        </Button>

        <Box w="40px">
          <DropdownMenu
            options={[
              {
                label: <FormattedMessage {...messages.delete} />,
                onClick: () => handleDeleteBackups(vaultInfo),
              },
            ]}
          />
        </Box>
      </Box>
    );
  };

  const renderRestoreButton = vaultInfo ? renderRestoreButtonVault : renderRestoreButtonUser;

  const kindPill =
    kind === BackupKind.automatic ? (
      <SquarePill size="sm" text={<FormattedMessage {...messages.automatic} />} color="orange" />
    ) : kind === BackupKind.archive ? (
      <span></span>
    ) : (
      <SquarePill size="sm" text={<FormattedMessage {...messages.manual} />} color="purple" />
    );

  const customerName = vaultInfo?.customer ? vaultInfo.customer.name : member.orgName;

  const customer = vaultInfo && (
    <Box d="row" w={12} a="center">
      <Icon name="nav customers" size="md" marginRight="sm" color="grey"></Icon>
      <Text color="grey" size="sm">
        {customerName}
      </Text>
    </Box>
  );

  const noteTooltip =
    kind === BackupKind.manual && description ? (
      <Tooltip variant="full" placement="bottom-start" button={<Icon name="notebook" />}>
        <Text size="sm" fullWidth marginBottom="xs">
          <FormattedMessage id="website_backups_card.backup_note" defaultMessage="Backup note:" />
        </Text>

        {description}
      </Tooltip>
    ) : null;

  const backupInfo = (
    <Fragment>
      <Box w="auto" grow={1} j="flex-start">
        {filesSize > 0 && (
          <Box px="sm" w={4} d="column" j="center" h="auto" minH={isMobile ? '30px' : '70px'} a="center">
            <Box d="row" a="center" w={12} wrap="nowrap">
              <Icon name="doc" marginRight="sm" size="lg" />
              <LegendNumber
                size={isMobile ? 'lg' : undefined}
                useEllipsis={false}
                numberText={getByteString(filesSize).split(' ')[0]}
                postfix={getByteString(filesSize).split(' ')[1]}
              />
            </Box>
          </Box>
        )}

        {totalDbsCount > 0 && (
          <Box px="sm" w={4} d="column" j="center" h="auto" minH={isMobile ? '30px' : '70px'} a="center">
            <Box d="row" a="center" wrap="nowrap">
              <Icon name="database" marginRight="sm" size="lg" />

              <Text size={isMobile ? 'lg' : '3xl'} weight="light">
                {totalDbsCount}
              </Text>
            </Box>
          </Box>
        )}

        {showEmails && emailsCount > 0 && (
          <Box px="sm" w={4} d="column" j="center" h="auto" minH={isMobile ? '30px' : '70px'} a="center">
            <Box d="row" a="center" wrap="nowrap">
              <Icon name="mail" marginRight="sm" size="lg" />

              <Text size={isMobile ? 'lg' : '3xl'} weight="light">
                {emailsCount}
              </Text>
            </Box>
          </Box>
        )}
      </Box>
    </Fragment>
  );

  if (isMobile) {
    return (
      <Fragment>
        {(filesSize > 0 || emailsCount > 0 || totalDbsCount > 0) && (
          <Card data-qa="mobileView">
            <Box g="lg" a="center">
              <Box g="lg" mb="lg">
                <Box w="auto" grow={1} d="column">
                  <Box mb="xs" w="auto">
                    <Span size="sm">{getBackupDateTime(startedAt)}</Span>
                  </Box>

                  <Box mb="xs">{kindPill}</Box>

                  <Box d="row">
                    {storageKind === BackupStorageKind.s3 && (
                      <SquarePill mr="xs" size="sm" text="S3" color="s3purple" />
                    )}
                    {noteTooltip}
                  </Box>

                  {customer}
                  {vaultInfo && (
                    <Span fullWidth size="md" highlightText={searchTerm} ellipsis>
                      {vaultInfo.domain}
                    </Span>
                  )}
                </Box>
                {renderRestoreButton()}
              </Box>

              <Box w={12} a="center" j="space-around" p="0">
                {backupInfo}
              </Box>
            </Box>
          </Card>
        )}
      </Fragment>
    );
  }

  return (
    <Fragment>
      {(filesSize > 0 || emailsCount > 0 || totalDbsCount > 0) && (
        <Card data-qa="desktopView">
          <Box g="lg" a="center">
            <Box w={3} a="center" g="lg">
              <Span fullWidth size="sm">
                {customer}
              </Span>

              {vaultInfo && (
                <Span fullWidth size="md" highlightText={searchTerm} ellipsis>
                  {vaultInfo.domain}
                </Span>
              )}

              <Span fullWidth size="sm">
                {getBackupDateTime(startedAt)}
              </Span>

              <Box w="auto" g="xs" a="center" wrap="nowrap" mt="xs">
                {kindPill}
                {storageKind === BackupStorageKind.s3 && <SquarePill size="sm" text="S3" color="s3purple" />}

                {noteTooltip}
              </Box>
            </Box>

            <Box w={9} a="center">
              {backupInfo}
              {renderRestoreButton()}
            </Box>
          </Box>
        </Card>
      )}
    </Fragment>
  );
};

export const WebsiteBackupCard = connect(mapStateToProps)(_WebsiteBackupCard);
